import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  
  // Admin data state
  const [adminData, setAdminData] = useState({
    classrooms: [],
    teachers: [],
    students: [],
    recentActivity: [],
    stats: {
      totalClassrooms: 0,
      totalTeachers: 0,
      totalStudents: 0,
      activeSessions: 0
    }
  });
  const [dataLoading, setDataLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Load all admin data
  const loadAdminData = async () => {
    if (dataLoaded || dataLoading) return; // Prevent duplicate calls
    
    try {
      setDataLoading(true);
      console.log('Loading admin data...');

      // Fetch all data in parallel
      const [classroomsResult, teachersResult, studentsResult, activityResult] = await Promise.all([
        // Fetch classrooms with pricing and teacher info
        supabase
          .from('classrooms')
          .select(`
            *, 
            classroom_pricing(price, payment_plan_id, id, payment_plans(id, name, billing_cycle)),
            teachers(user_id, users(first_name, last_name))
          `),
        
        // Fetch teachers with user details
        supabase
          .from('teachers')
          .select(`
            *,
            users(first_name, last_name, email, phone)
          `)
          .eq('status', 'active'),
        
        // Fetch students with user details
        supabase
          .from('students')
          .select(`
            *,
            users(first_name, last_name, email, phone)
          `)
          .eq('status', 'active'),
        
        // Fetch recent activity
        supabase
          .from('audit_logs')
          .select(`
            id,
            action_type,
            table_name,
            description,
            created_at,
            users(first_name, last_name)
          `)
          .order('created_at', { ascending: false })
          .limit(5)
      ]);

      // Handle results and errors
      const classrooms = classroomsResult.data || [];
      const teachers = teachersResult.data || [];
      const students = studentsResult.data || [];
      const recentActivity = activityResult.data || [];

      if (classroomsResult.error) console.error('Error fetching classrooms:', classroomsResult.error);
      if (teachersResult.error) console.error('Error fetching teachers:', teachersResult.error);
      if (studentsResult.error) console.error('Error fetching students:', studentsResult.error);
      if (activityResult.error) console.error('Error fetching activity:', activityResult.error);

      // Calculate stats
      const stats = {
        totalClassrooms: classrooms.filter(c => c.is_active).length,
        totalTeachers: teachers.length,
        totalStudents: students.length,
        activeSessions: 0 // We'll implement this later
      };

      // Update state
      setAdminData({
        classrooms,
        teachers,
        students,
        recentActivity,
        stats
      });

      setDataLoaded(true);
      console.log('Admin data loaded successfully');

    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setDataLoading(false);
    }
  };

  // Refresh specific data type
  const refreshData = async (dataType) => {
    try {
      console.log(`Refreshing ${dataType} data...`);
      
      switch (dataType) {
        case 'classrooms':
          const { data: classrooms } = await supabase
            .from('classrooms')
            .select(`
              *, 
              classroom_pricing(price, payment_plan_id, id, payment_plans(id, name, billing_cycle)),
              teachers(user_id, users(first_name, last_name))
            `);
          setAdminData(prev => ({ ...prev, classrooms: classrooms || [] }));
          break;
          
        case 'teachers':
          const { data: teachers } = await supabase
            .from('teachers')
            .select(`*, users(first_name, last_name, email, phone)`)
            .eq('status', 'active');
          setAdminData(prev => ({ ...prev, teachers: teachers || [] }));
          break;
          
        case 'students':
          const { data: students } = await supabase
            .from('students')
            .select(`*, users(first_name, last_name, email, phone)`)
            .eq('status', 'active');
          setAdminData(prev => ({ ...prev, students: students || [] }));
          break;
          
        default:
          await loadAdminData();
      }
    } catch (error) {
      console.error(`Error refreshing ${dataType}:`, error);
    }
  };

  // Check for existing auth on mount
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const adminStatus = localStorage.getItem('is_admin') === 'true';
    
    if (token && adminStatus) {
      setAuthToken(token);
      setIsAdmin(true);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        
        setAuthToken(data.token);
        setIsAdmin(true);
        
        // Store in localStorage
        localStorage.setItem('admin_token', data.token);
        localStorage.setItem('is_admin', 'true');
        
        return { success: true };
      } else {
        return { success: false, error: 'Invalid credentials' };
      }
    } catch (error) {
      return { success: false, error: 'Login failed. Please try again.' };
    }
  };

  const logout = () => {
    setIsAdmin(false);
    setIsEditMode(false);
    setAuthToken(null);
    
    // Clear localStorage
    localStorage.removeItem('admin_token');
    localStorage.removeItem('is_admin');
  };

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  const updateContent = async (key, value) => {
    if (!authToken) return false;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({ key, value }),
      });

      return response.ok;
    } catch (error) {
      console.error('Failed to update content:', error);
      return false;
    }
  };

  const getContent = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content`);
      if (response.ok) {
        return await response.json();
      }
      return {};
    } catch (error) {
      console.error('Failed to fetch content:', error);
      return {};
    }
  };

  const value = {
    isAdmin,
    isEditMode,
    authToken,
    login,
    logout,
    toggleEditMode,
    updateContent,
    getContent,
    // Admin data management
    adminData,
    dataLoading,
    dataLoaded,
    loadAdminData,
    refreshData
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export { AdminContext };